import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { BasePaneComponent, IBasePaneTab } from '@app/_v3/components/base-pane.component';
import { CollectionCardModule } from '@app/collections';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { LibraryElementsModule } from '@app/library/library-elements.module';
import { ERedPointsEvents } from '@app/store/models';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import { NoContentStubComponent } from '@core/components/no-content-stub/no-content-stub.component';
import { RedPointDirective } from '@core/components/red-points/red-point/red-point.directive';
import { SpinnersModule } from '@core/components/spinners/spinners.module';
import { RedPointsService } from '@core/services/red-points.service';
import { LibraryRouter } from '@models/library.router';
import { IconComponent } from '@shared/icon/icon.component';
import { StatusBadgeComponent } from '@shared/layouts/status-badge/status-badge.component';
import { Collection } from 'lingo2-models';
import { InViewportModule } from 'ng-in-viewport';
import { OnUiButtonModule } from 'onclass-ui';
import { tabs as rawTabs } from './constants';
import { LibraryCollectionsMiniComponent } from './library-collections-mini/library-collections-mini.component';
import { ICollectionTab } from './models';

export function remapTabs(
  tabs: ICollectionTab[],
  role: 'tutor' | 'student' | 'guest' = 'tutor',
): { paneTabs: IBasePaneTab[]; filtersByCode: Record<string, any> } {
  const filtersByCode: Record<string, any> = {};
  const paneTabs: IBasePaneTab[] = tabs.map((t) => {
    filtersByCode[t.id] = t.filter;

    return {
      code: t.id,
      title: t.title,
      stub: role === 'guest' ? t.stub?.stub_guest : role === 'tutor' ? t.stub?.tutor : t.stub?.student,
      stub_guest: t.stub?.stub_guest,
      id: t.id,
    };
  });

  return { paneTabs, filtersByCode };
}

@Component({
  selector: 'app-my-library-collections',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    LocaleSharedModule,
    LibraryElementsModule,
    CollectionCardModule,
    SpinnersModule,
    OnUiButtonModule,
    ModalModule,
    MobileModalModule,
    RedPointDirective,
    StatusBadgeComponent,
    NoContentStubComponent,
    IconComponent,
    InViewportModule,
    LibraryCollectionsMiniComponent,
  ],
  templateUrl: './my-library-collections.component.html',
  styleUrls: ['./my-library-collections.component.scss', '../../panes/filter-content/filter-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyLibraryCollectionsComponent extends BasePaneComponent<IBasePaneTab> {
  protected readonly router = inject(Router);
  protected readonly activatedRoute = inject(ActivatedRoute);
  protected readonly redPointsService = inject(RedPointsService);

  public openedCollection: Collection;

  // Map tabs to IBasePaneTab
  public override tabs: IBasePaneTab[] = [];
  private filtersByCode: Record<string, any> = {};
  private getRole(): 'tutor' | 'student' | 'guest' {
    if (this.isGuest) return 'guest';
    return this.isAsIfTeacher ? 'tutor' : 'student';
  }

  /*
   */
  constructor() {
    super();
    const { paneTabs, filtersByCode } = remapTabs(rawTabs, this.getRole());
    this.tabs = paneTabs;
    this.filtersByCode = filtersByCode;
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.initTab();
  }

  /*
   */
  public goCreateCollection(): void {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/content/collections']));
    window.open(url, '_blank');
  }

  /*
   */
  public onClickedCard(): void {
    this.resetPane();
    this.loadPane();
  }

  /*
   */
  public onToggleCollectionInModal(collection: Collection = null): void {
    this.openedCollection = collection;
    this.detectChanges();
  }

  /*
   */
  public trackByFn(index: number): number {
    return index;
  }

  /*
   */
  protected override getEntityRoute(entity: Collection): string {
    return LibraryRouter.collectionRouteUniversal(entity);
  }

  /*
   */
  protected override createNewEntity(): Collection {
    return new Collection();
  }

  /*
   */
  protected override redPointEvent(tab: IBasePaneTab): ERedPointsEvents {
    switch (tab.code) {
      case 'published':
        return ERedPointsEvents.my_collection_published;
      case 'moderation':
        return ERedPointsEvents.my_collection_moderation;
      case 'purchased':
        return ERedPointsEvents.collection_purchased;
      default:
        return null;
    }
  }

  /*
   */
  protected override setupFilter(tab: IBasePaneTab): void {
    this._pane = {
      ...this._pane,
      loaded: false,
      pagination: { page: 1, pageSize: 10 },
      collectionsFilter: this.filtersByCode[tab.id],
      collections: [],
    };
  }

  /*
   */
  public goSignup(): void {
    if (this.isGuest) {
      this.authService.showAuthModal();
    }
  }
}
